import { useTranslator } from '@translator-app/react';
import NextLink from 'next/link';
import React from 'react';

import { ANDROID_APP_ID, IOS_APP_ID } from '../../constants';
import { useKioskMode } from '../../contexts/kiosk-context';
import MailIcon from '../../icons/mail.svg';
import PhoneIcon from '../../icons/phone.svg';

const WOYTI_MERCHANT_URL = 'https://woyti.com';
const WOYTI_CONSUMER_URL = 'https://woyti.com/consument';
const SUPPORT_EMAIL = 'info@viresmo.com';
const SUPPORT_PHONE = '+32496368715';
const VAT_NUMBER = 'BE 0654.941.327';

const MERCHANT_NAV = [
  { hash: 'hoe-werkt-het', labelKey: 'siteNav.howItWorks' },
  { hash: 'voordelen', labelKey: 'siteNav.benefits' },
  { hash: 'versbox', labelKey: 'siteNav.versbox' },
  { hash: 'prijzen', labelKey: 'siteNav.pricing' },
  { hash: 'contact', labelKey: 'siteNav.contact' },
] as const;

export const SiteFooter: React.FC = () => {
  const { translate } = useTranslator();
  const { isKioskMode } = useKioskMode();

  if (isKioskMode) {
    return null;
  }

  return (
    <footer className="bg-dark text-white/70">
      <div className="mx-auto max-w-7xl px-6 py-14">
        <div className="grid gap-10 md:grid-cols-4">
          <div className="md:col-span-2">
            <p className="font-display-bold text-2xl tracking-tight text-white">WOYTI</p>
            <p className="mt-2 text-sm text-primary">{translate('siteFooter.tagline')}</p>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-white/60">{translate('siteFooter.description')}</p>

            <div className="mt-5 space-y-1.5 text-sm text-white/60">
              <p className="flex items-center gap-2">
                <MailIcon className="h-4 w-4 text-primary" aria-hidden />
                <a href={`mailto:${SUPPORT_EMAIL}`} className="hover:text-white">
                  {SUPPORT_EMAIL}
                </a>
              </p>
              <p className="flex items-center gap-2">
                <PhoneIcon className="h-4 w-4 text-primary" aria-hidden />
                <a href={`tel:${SUPPORT_PHONE}`} className="hover:text-white">
                  +32 496 36 87 15
                </a>
              </p>
              <p className="text-white/40">{VAT_NUMBER}</p>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href={`https://apps.apple.com/be/app/woyti/id${IOS_APP_ID}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-4 py-2.5 text-xs font-medium text-white transition hover:bg-white/20"
              >
                <span className="flex flex-col leading-none">
                  <span className="text-[9px] opacity-70">{translate('siteFooter.appStorePrefix')}</span>
                  <span className="text-sm font-semibold">{translate('siteFooter.appStore')}</span>
                </span>
              </a>
              <a
                href={`https://play.google.com/store/apps/details?id=${ANDROID_APP_ID}&hl=nl`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-4 py-2.5 text-xs font-medium text-white transition hover:bg-white/20"
              >
                <span className="flex flex-col leading-none">
                  <span className="text-[9px] opacity-70">{translate('siteFooter.playStorePrefix')}</span>
                  <span className="text-sm font-semibold">{translate('siteFooter.playStore')}</span>
                </span>
              </a>
            </div>
          </div>

          <div>
            <h4 className="mb-4 text-sm font-semibold text-white">{translate('siteFooter.navigation')}</h4>
            <ul className="space-y-2 text-sm">
              {MERCHANT_NAV.map((item) => (
                <li key={item.hash}>
                  <a href={`${WOYTI_MERCHANT_URL}/#${item.hash}`} className="hover:text-white">
                    {translate(item.labelKey)}
                  </a>
                </li>
              ))}
              <li>
                <a href={WOYTI_CONSUMER_URL} className="hover:text-white">
                  {translate('siteFooter.forCustomers')}
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="mb-4 text-sm font-semibold text-white">{translate('siteFooter.legal')}</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <NextLink href="/policies/privacy-policy" className="hover:text-white">
                  {translate('footer.privacyPolicy')}
                </NextLink>
              </li>
              <li>
                <NextLink href="/policies/terms-conditions" className="hover:text-white">
                  {translate('footer.termsConditions')}
                </NextLink>
              </li>
              <li>
                <NextLink href="/policies/cookie-policy" className="hover:text-white">
                  {translate('footer.cookiePolicy')}
                </NextLink>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 border-t border-white/10 pt-8 text-center text-sm text-white/40">
          <p>
            &copy; {new Date().getFullYear()} Woyti. {translate('siteFooter.rights')}
          </p>
          <p className="mt-1">{translate('siteFooter.madeWithLove')}</p>
        </div>
      </div>
    </footer>
  );
};
